import {
  Button,
  Dialog,
  DialogActions,
  DialogContent
} from '@material-ui/core';
import { PauseCircle, PlayCircle } from 'lucide-react';

import { DialogTitleWithTypography } from '../../../../components/DialogTitleWithTypography';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../i18next';

/**
 * Confirmation dialog for pausing ("deactivate") or resuming ("reactivate") a
 * timeline item. Controlled — the caller owns `open` and the trigger, so it works
 * for both the classic top-bar button and the NEW_UX overflow-menu item. On
 * confirm it closes and calls `onConfirm`, which the parent wires to set
 * `deactivated` on the item model and persist it through the normal save path.
 * Deactivation intentionally bypasses the Save button's content validation — you
 * can pause an item without it being complete.
 *
 * Backend effect (see engine `getEffectiveAudiences`): a deactivated item stops
 * materializing for new enrollments and is dropped from instances where it has
 * not started yet, while started + completed items are preserved. Reversible.
 */
export const DeactivateItemConfirmDialog = ({
  open,
  deactivated,
  onClose,
  onConfirm
}: {
  open: boolean;
  deactivated?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const isDeactivated = !!deactivated;
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithTypography>
        {isDeactivated
          ? t('Reactivate this timeline item?')
          : t('Deactivate this timeline item?')}
      </DialogTitleWithTypography>
      <DialogContent>
        {isDeactivated
          ? t(
              `This item will be added to new experiences, and to currently running experiences where it's scheduled in the future.`
            )
          : t(
              `Deactivated items will not be added to any new experiences, and will be removed from currently running experiences where the item has not yet started. It will stay active in any experience where it's already started. You can reactivate it at any time.`
            )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          color={isDeactivated ? 'primary' : 'secondary'}
          variant="contained"
          onClick={() => {
            onClose();
            onConfirm();
          }}
        >
          {isDeactivated ? t('Reactivate') : t('Deactivate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Classic top-bar control for an "Edit timeline item" modal (task / message /
 * automation): a Deactivate/Reactivate button that opens
 * {@link DeactivateItemConfirmDialog}. NEW_UX drives the same confirm dialog from
 * the overflow menu instead of this button.
 */
export const DeactivateItemButton = ({
  deactivated,
  onConfirm
}: {
  deactivated?: boolean;
  onConfirm: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const dialog = useDialogState(false);
  const isDeactivated = !!deactivated;

  return (
    <>
      <Button
        variant="outlined"
        // Match the destructive "Delete" button (red outline) for Deactivate;
        // Reactivate is non-destructive so it stays neutral/primary.
        color={isDeactivated ? 'primary' : 'secondary'}
        startIcon={
          isDeactivated ? <PlayCircle size={16} /> : <PauseCircle size={16} />
        }
        onClick={dialog.openDialog}
      >
        {isDeactivated ? t('Reactivate') : t('Deactivate')}
      </Button>
      <DeactivateItemConfirmDialog
        open={dialog.dialogOpen}
        deactivated={deactivated}
        onClose={dialog.closeDialog}
        onConfirm={onConfirm}
      />
    </>
  );
};
